(() => {
  if (customElements.get('hoodie-feedback')) return;

  const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSfKIGYzNXKRv7_bvSoB7ilMRQMMMtpN2vMZXZtnzl2DTne5Zw/viewform';
  let privacyLoading = false;

  function isExcludedCampaign() {
    return new URL(location.href).searchParams.getAll('utm_campaign').includes('exp083_field');
  }

  function analyticsAllowed() {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  }

  function initializePrivacy() {
    if (window.Shopify?.customerPrivacy || privacyLoading || !window.Shopify?.loadFeatures) return;
    privacyLoading = true;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
      privacyLoading = false;
      // Consent becoming available never loads a form. A fresh click is required.
      document.dispatchEvent(new CustomEvent('hoodie-feedback:privacy-ready'));
    });
  }

  function isVisible(element) {
    const style = getComputedStyle(element);
    return !element.hidden && style.display !== 'none' && style.visibility !== 'hidden' &&
      parseFloat(style.opacity) !== 0 && element.getClientRects().length > 0;
  }

  function productPath(url) {
    try {
      return new URL(url, location.origin).pathname.split('/products/')[1]?.replace(/\/$/, '') || '';
    } catch {
      return '';
    }
  }

  customElements.define('hoodie-feedback', class HoodieFeedback extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.productInfo = this.closest('product-info');
      if (!this.isCurrentPDP()) return;
      this.events = new AbortController();
      this.teaser = this.querySelector('[data-feedback-open]');
      const options = { signal: this.events.signal };

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-feedback-open]');
        if (opener) this.openForm(opener);
      }, options);
      window.addEventListener('popstate', () => this.updatePDP(), options);
      window.addEventListener('pagehide', () => this.closeForm(false), options);
      window.addEventListener('pageshow', () => this.updatePDP(), options);
      const onConsent = () => this.updateConsent();
      document.addEventListener('visitorConsentCollected', onConsent, options);
      document.addEventListener('hoodie-feedback:privacy-ready', onConsent, options);
      initializePrivacy();
      this.updatePDP();
    }

    disconnectedCallback() {
      this.closeForm(false);
      this.events?.abort();
      this.events = null;
    }

    isCurrentPDP() {
      const info = this.productInfo;
      const main = this.closest('main#MainContent');
      if (!this.isConnected || !main || !info || info.dataset.updateUrl !== 'true' || info.dataset.originalSection) return false;
      if (!info.id.startsWith('MainProduct-') || this.closest('quick-add-modal, quick-add-bulk')) return false;
      const currentPath = productPath(location.href);
      if (!currentPath || currentPath !== productPath(this.dataset.productUrl) || currentPath !== productPath(info.dataset.url)) return false;
      const candidates = [...main.querySelectorAll('product-info[id^="MainProduct-"][data-update-url="true"] hoodie-feedback')];
      return candidates.find((element) => productPath(element.dataset.productUrl) === currentPath) === this;
    }

    productName() {
      if (!this.isCurrentPDP()) return '';
      return this.productInfo.querySelector('.product__title h1')?.textContent.trim() || '';
    }

    otherDialogOpen() {
      return [...document.querySelectorAll(
        'dialog[open], modal-dialog[open], cart-drawer.active, .cart-notification.active, ' +
        '[role="dialog"], [role="alertdialog"], [aria-modal="true"], ' +
        '#shopify-pc__banner, #shopify-pc__prefs, menu-drawer details[open]'
      )].some((element) => element !== this.dialog && !this.dialog?.contains(element) && isVisible(element));
    }

    updatePDP() {
      if (!this.events) return;
      this.hidden = isExcludedCampaign() || !this.isCurrentPDP() || !this.productName();
      if (this.hidden) this.closeForm(false);
    }

    openForm(opener) {
      const name = this.productName();
      if (isExcludedCampaign() || !name || this.dialog || this.otherDialogOpen()) return;
      const url = new URL(FORM_URL);
      url.searchParams.set('embedded', 'true');
      url.searchParams.set('entry.1842036402', name);
      this.formURL = url.href;
      this.opener = opener;
      this.dialog = this.querySelector('[data-feedback-dialog]').content.firstElementChild.cloneNode(true);
      const dialog = this.dialog;
      document.body.appendChild(dialog);
      dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-feedback-close]')) this.closeForm();
        if (event.target.closest('[data-feedback-cookie-settings]')) this.closeForm(false);
        if (event.target.closest('[data-feedback-load]')) this.loadForm();
        if (event.target.closest('[data-feedback-separate]') && !analyticsAllowed()) {
          event.preventDefault();
          this.updateConsent();
        }
      });
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeForm();
      });
      dialog.addEventListener('close', () => {
        if (this.dialog === dialog) this.closeForm();
      });
      this.lockScroll();
      try {
        dialog.showModal();
        this.updateConsent();
        this.loadForm();
      } catch {
        this.closeForm();
      }
    }

    updateConsent() {
      if (!this.dialog) return;
      const allowed = analyticsAllowed();
      const separate = this.dialog.querySelector('[data-feedback-separate]');
      separate.setAttribute('aria-disabled', String(!allowed));
      if (allowed) separate.href = this.formURL;
      else separate.removeAttribute('href');
      this.dialog.querySelector('[data-feedback-load]').hidden = !allowed;
      this.dialog.querySelector('[data-feedback-cookie-settings]').hidden = allowed;
      this.dialog.querySelector('[data-feedback-consent-message]').textContent = allowed
        ? 'Das Google-Formular ist freigegeben. Du kannst es jetzt laden.'
        : 'Deine Cookie-Einstellungen erlauben das Google-Formular noch nicht. Dafür ist die Freigabe für Analysen erforderlich.';
      if (!allowed) {
        this.dialog.querySelector('[data-feedback-frame]').replaceChildren();
        this.dialog.querySelector('[data-feedback-frame]').hidden = true;
        this.dialog.querySelector('[data-feedback-message]').hidden = false;
      }
    }

    loadForm() {
      if (!this.dialog || isExcludedCampaign() || !this.isCurrentPDP() || !analyticsAllowed()) return;
      const frame = this.dialog.querySelector('[data-feedback-frame]');
      if (frame.firstElementChild) return;
      const iframe = document.createElement('iframe');
      iframe.title = `Google-Formular: Deine Meinung zu diesem Hoodie – ${this.productName()}`;
      iframe.referrerPolicy = 'no-referrer';
      iframe.src = this.formURL;
      frame.appendChild(iframe);
      frame.hidden = false;
      this.dialog.querySelector('[data-feedback-message]').hidden = true;
      // A cross-origin load event cannot prove either loading success or answer submission.
    }

    lockScroll() {
      this.scrollPosition = { left: window.scrollX, top: window.scrollY };
      const style = document.body.style;
      this.savedBodyStyles = {};
      this.appliedBodyStyles = {
        position: 'fixed', top: `-${window.scrollY}px`, left: `-${window.scrollX}px`,
        width: '100%', overflow: 'hidden',
        paddingRight: `${parseFloat(getComputedStyle(document.body).paddingRight) + Math.max(0, window.innerWidth - document.documentElement.clientWidth)}px`,
      };
      Object.entries(this.appliedBodyStyles).forEach(([property, value]) => {
        this.savedBodyStyles[property] = style[property];
        style[property] = value;
      });
    }

    closeForm(restoreFocus = true) {
      const dialog = this.dialog;
      if (!dialog) return;
      this.dialog = null;
      if (dialog.open) dialog.close();
      dialog.remove();
      Object.entries(this.savedBodyStyles || {}).forEach(([property, value]) => {
        if (document.body.style[property] === this.appliedBodyStyles[property]) document.body.style[property] = value;
      });
      window.scrollTo({ ...this.scrollPosition, behavior: 'instant' });
      const target = this.opener?.isConnected && isVisible(this.opener) ? this.opener : this.teaser;
      if (restoreFocus && target?.isConnected && this.isCurrentPDP()) target.focus({ preventScroll: true });
      this.opener = null;
    }
  });
})();
